import type { AllowlistRole } from '@bbbk/schema';
import { DEFAULT_PATH, viewToPath, pathToView } from '../pages/routeMap';

// WHERE A SIGNED-IN ACCOUNT BELONGS.
//
// Freddi, 2026-09-14: "if its a desk account it should load the today page, if
// its a customer account it should load up customer stays page so there should
// never be an in between state."
//
// THE ROLE IS KNOWN BEFORE ANYTHING RENDERS — the session probe has already
// answered — so a wrong-door landing is a redirect, never a page. Opening
// `/home` at the desk used to mount the customer app, which asked the API,
// was told 403, and drew *This account is a desk account* with a button to
// the place it could have sent you. A door is for someone whose destination
// you do not know; this app always knows.
//
// ONE FUNCTION, so the four surfaces that ask (App's customer branch, the
// desk shell, the staff router, the customer router) cannot answer
// differently. Pure — the whole of it is a unit test.

/** The customer's own app: their stays. */
export const CUSTOMER_HOME = '/home';
/** The registration steps, which Home itself decides between. */
export const CUSTOMER_REGISTER = '/register';
/** The request wizard (request-a-stay). Its own URL, so the browser's Back leaves it. */
export const CUSTOMER_BOOK = '/book';
/** Info (customer-home-stays), which carries the dogs. Stays is Home itself — one level, no stay page, no dogs page. */
export const CUSTOMER_INFO = '/info';
/** The pay sheet (deposit-proof): `/pay/<stay id>`, its own URL so the browser's Back closes it. */
export const CUSTOMER_PAY = '/pay';
export const payPath = (stayId: string) => `${CUSTOMER_PAY}/${encodeURIComponent(stayId)}`;
// The dogs drawer (vaccination-cards AC1): over Home at its own URL, as the pay sheet is; `?dog=` names the row to open.
// NOT `/dogs`, which the story named: that is the desk's Dogs & Owners, and claiming it for the customer
// would hand a signed-out desk user on their own Dogs page the customer's sign-in.
export const CUSTOMER_DOGS = '/your-dogs';
export const dogsPath = (dogId?: string | null) => (dogId ? `${CUSTOMER_DOGS}?dog=${encodeURIComponent(dogId)}` : CUSTOMER_DOGS);
/** The staff self-view — the only page a `staff` role ever sees. */
export const STAFF_PATH = '/my-shifts';

/** The customer app's own URLs. */
const CUSTOMER_PATHS = new Set<string>([CUSTOMER_HOME, CUSTOMER_REGISTER, CUSTOMER_BOOK, CUSTOMER_INFO, CUSTOMER_DOGS]);

/**
 * Paths NOBODY is routed away from, whatever their role.
 *
 * A registration link is a real screen about a real token — flow 1.6 turns an
 * old one into the sign-in page, and routing it would hide what the holder
 * came to read. The callback's own error pages are reached without a session
 * and explain something the role cannot.
 */
function isDoor(path: string): boolean {
  return path.startsWith('/join/') || path === '/signin-error' || path === '/denied';
}

/**
 * Where this role should be sent from this path, or null to stay put.
 *
 * `null` role is "not resolved yet" — there is nothing to decide on, and the
 * caller is still showing its loading frame.
 */
export function landingFor(role: AllowlistRole | null, path: string): string | null {
  if (role === null || isDoor(path)) return null;

  if (role === 'customer') {
    // Their own app, and the sign-in page is behind them the moment they have
    // a session — signing in again is not a thing to offer someone signed in.
    // The pay sheet's URL carries a stay id (deposit-proof): a customer path by prefix.
    return CUSTOMER_PATHS.has(path) || path.startsWith(CUSTOMER_PAY + '/') ? null : CUSTOMER_HOME;
  }

  if (role === 'staff') {
    // staff-self-view AC1: neither the desk shell nor the customer app.
    return path === STAFF_PATH ? null : STAFF_PATH;
  }

  // A DESK ROLE.
  return deskLanding(path);
}

/**
 * The desk's own half of the rule, for the shell — which knows it is the desk
 * and should not have to hand a role in to be told so.
 *
 * The customer app's URLs, the bare root, a typo: all land on the default
 * section. THE URL ALWAYS NAMES THE PAGE — rendering Today while the address
 * bar still reads `/home` is the in-between state wearing a different hat.
 */
export function deskLanding(path: string): string | null {
  if (isDoor(path)) return null;
  return namesASection(path) ? null : DEFAULT_PATH;
}

/** Does this path name a section of the desk? `/` and a typo do not. */
function namesASection(path: string): boolean {
  return viewToPath(pathToView(path)) === path;
}
